import { APIDataDocument, APIDataDocumentSchemaRevision } from '@evertel/types'
import { injectable, inject, decorate } from '@evertel/di'
import { action, computed, makeAutoObservable, makeObservable, observable, runInAction } from 'mobx'
// evertel
import { Api } from '@evertel/api'
import { DocumentStore, DocumentSchemaStore, DocumentSchemaRevisionStore } from '../store'
import moment from 'moment'

class DocumentViewController {
    documentId = 0
    _name = ''
    documentSchemaId = 0
    documentSchemaRevisionId = 0
    loaded = false
    deleted = false
    myAccess: any = {}
    canEdit = false
    canShare = false

    constructor(
        protected documentStore: DocumentStore,
        protected documentSchemaStore: DocumentSchemaStore,
        protected documentSchemaRevisionStore: DocumentSchemaRevisionStore,
        protected api: Api
    ) {
        //MUST be makeObservable, makeAutoObservable breaks extended classes (DocumentEditController extends this)
        makeObservable(this, {
            documentId: observable,
            _name: observable,
            name: computed,
            documentSchemaId: observable,
            documentSchemaRevisionId: observable,
            loaded: observable,
            deleted: observable,
            myAccess: observable,
            canEdit: observable,
            canShare: observable,
            load: action,
            loadFromStoreOrFetch: action,
            setArchive: action,
            delete: action,
            document: computed,
            documentSchema: computed
        })
    }

    get name(): string {
        return this.document?.name?.trim()
    }

    async load(documentId: number, ...args: any[]) {
        if (!documentId) throw new Error('Missing a document ID!')

        this.loaded = false

        const document = await this.fetchDocument(documentId)

        if (!document) return

        this.processLoadedDocument(document)

        runInAction(() => {
            this.loaded = true
        })

        return this.document
    }

    protected processLoadedDocument(document: APIDataDocument) {
        this.documentStore.update(document)
        this.documentSchemaStore.update(document?.documentSchema)
        this.documentSchemaRevisionStore.update(document?.documentSchemaRevision)

        runInAction(() => {
            this.documentId = document?.id || 0
            this.documentSchemaId = document?.documentSchemaId || 0
            this.documentSchemaRevisionId = document?.documentSchemaRevisionId || 0
        })
    }

    loadFromStoreOrFetch = async (documentId: number) => {
        if (!documentId) throw new Error('Missing a document ID!')

        this.loaded = false
        const document = this.documentStore.findById(documentId)

        const fiveMinOld = document?.fetchedDate < moment().subtract(5, 'minutes')

        if (!document || fiveMinOld) {
            // console.log(':::::: DocumentViewController: Unable to find cached document, loading.', documentId)
            //no document or too old, so fetch again
            return await this.load(documentId)
        }
        // console.log(':::::: DocumentViewController: 🎈🎈🎈🎈🎈🎈', documentId)

        // if it was too old it intentionally still falls through to here
        // so we can show the older info while the new info is updated
        // in that case this.loaded might be ambiguous...

        runInAction(() => {
            this.documentId = documentId || 0
            this.documentSchemaId = document?.documentSchemaId || 0
            this.documentSchemaRevisionId = document?.documentSchemaRevisionId || 0
            this.loaded = true
        })

        return this.document

    }

    protected getDefaultIncludes(): string[] | Array<{relation: string, scope?: {include: string}}> {
        return [
            'media',
            'documentSchema',
            'documentSchemaRevision'
        ]
    }

    fetchDocument = async (documentId: number): Promise<APIDataDocument> => {
        return await this.api.Routes.Document.getById(documentId, {
            include: this.getDefaultIncludes()
        })
    }

    fetchDocumentRevisions = async (documentSchemaId: number, filter: any): Promise<APIDataDocumentSchemaRevision[]> => {
        return await this.api.Routes.DocumentSchema.getRevisions(documentSchemaId, filter)
    }

    setArchive = async (isArchived: boolean): Promise<APIDataDocument> => {
        const response = await this.api.Routes.Document.putById(this.documentId, { isArchived })

        runInAction(() => {
            this.documentStore.update(response)
        })

        return this.documentStore.findById(response.id as number) as APIDataDocument
    }

    delete = async () => {
        // can only delete documents not previously shared
        const resp = await this.api.Routes.Document.delById(this.documentId)

        runInAction(() => {
            // if deleted, remove from the DocumentStore
            if (resp?.count) {
                // Give UI enough time to navigate away from screen.
                // Without this timeout, mobx triggers the effects that attempt to
                // reload or fetch this document id, which causes error alerts to pop up.
                this.deleted = true
                setTimeout(() => {
                    this.documentStore.deleteById(this.documentId)
                }, 100)
            }
        })
    }

    cloneDocument = (): Promise<APIDataDocument> => {
        return this.api.Routes.Document.postClone(this.documentId)
    }

    downloadCSV = async (documentId: number): Promise<Blob> => {
        // @ts-expect-error we treat it as a blob
        return this.api.Routes.Document.getDownloadCsv(documentId)
    }

    trackPdfDownload = async (documentId: number): Promise<any> => {
        return this.api.Routes.Document.putHistoryPdfDownload(documentId)
    }

    get document() {
        return this.documentStore.findById(this.documentId)
    }

    get documentSchema() {
        // returns the schema at the revision version of the doc
        if (this.documentSchemaRevisionId) {
            const revision = this.documentSchemaRevisionStore?.findById(this.documentSchemaRevisionId)
            if (revision) {
                // merge the revision with the rest of the schema data
                return {
                    ...this.documentSchemaStore.findById(this.documentSchemaId),
                    schema: revision.schema
                }
            } else {
                return this.documentSchemaStore.findById(this.documentSchemaId)
            }
        } else {
            return this.documentSchemaStore.findById(this.documentSchemaId)
        }
    }
}

decorate(injectable(), DocumentViewController)
decorate(inject(DocumentStore), DocumentViewController, 0)
decorate(inject(DocumentSchemaStore), DocumentViewController, 1)
decorate(inject(DocumentSchemaRevisionStore), DocumentViewController, 2)
decorate(inject(Api), DocumentViewController, 3)

export { DocumentViewController }
